import { getStoredAuthToken } from '../auth/storage';
import { ApiError, apiRequest, createTimeoutAbort, DEFAULT_API_TIMEOUT_MS } from './client';
import { API_CREDENTIALS } from './http-credentials';
import type {
  AuthUser,
  Campaign,
  Character,
  CoinBalance,
  GuestLoginRequest,
  GuestLoginResponse,
  JsonRecord,
  LoginResponse,
  PreviewGreetingRequest,
  PreviewGreetingResponse,
  RandomFillField,
  RandomFillResponse,
  VoiceTraitsResponse,
  SyncCursor,
  TableRound,
  UpgradeCredentialsRequest,
  UpgradeCredentialsResponse,
  UserSettings,
  WorldElement,
  WorldElementCharacter,
  WorldRelationship,
  WorldTree,
  WorldTreeLineSetting,
  WorldTreeNode,
} from './types';

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue>;

export type RelationshipEndpointType = 'world_element' | 'character';

export interface WorldRelationshipTypeOptions {
  allowed: boolean;
  categories: Record<string, string[]>;
}

export interface CreateWorldRelationshipInput {
  sourceType: RelationshipEndpointType;
  sourceId: number | string;
  targetType: RelationshipEndpointType;
  targetId: number | string;
  category: string;
  relationType: string;
  showInEditor?: boolean;
}

export interface CreateWorldRelationshipResponse {
  id: number;
  [key: string]: unknown;
}

export interface SyncEntityChanges<T> {
  upserts: T[];
  deletions: number[];
}

export interface AssetSizesResponse {
  sizes: Record<string, number | null>;
}

/**
 * W5-E: Verweis auf eine Zeile des geteilten Vorlagenkatalogs. Der Server
 * schickt nur noch ID + den Kosmos DES NUTZERS; der Inhalt liegt bereits
 * kontouebergreifend im Geraet (GET /api/catalog/v/:version).
 */
export interface CatalogVisibleRef {
  id: number;
  kosmos_id: number | null;
}

export interface SyncCatalogBlock {
  version: number;
  /** Die lokale Katalogversion passt nicht mehr — Nutzlast neu laden. */
  stale: boolean;
  /** `visible` ist die VOLLSTAENDIGE Menge und ersetzt den lokalen Stand. */
  full: boolean;
  visible: CatalogVisibleRef[];
  hidden: number[];
}

export interface SyncChangesResponse {
  cursor: number;
  hasMore: boolean;
  changes: {
    campaign: SyncEntityChanges<Campaign>;
    character: SyncEntityChanges<Character>;
    world_element: SyncEntityChanges<WorldElement>;
    world_relationship: SyncEntityChanges<WorldRelationship>;
    world_tree: SyncEntityChanges<WorldTree>;
    world_tree_node: SyncEntityChanges<WorldTreeNode>;
    table_round: SyncEntityChanges<TableRound>;
  };
  /** Nur vorhanden, wenn die Anfrage `catalog=<version>` getragen hat. */
  catalog?: SyncCatalogBlock;
}

export interface CatalogManifest {
  version: number;
  elementCount: number;
  relationCount: number;
  hash: string | null;
  builtAt: string | null;
  lang: string;
}

export interface CatalogPayloadResponse {
  version: number;
  lang: string;
  elementCount: number;
  relationCount: number;
  elements: WorldElement[];
  relationships: WorldRelationship[];
}

export interface WorldTreeSummary {
  id: number;
  name: string;
  mode: string;
  description: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface WorldTreeFull extends WorldTree {
  nodes: WorldTreeNode[];
  elements: WorldElement[];
  relationships: WorldRelationship[];
  lineSettings: WorldTreeLineSetting[];
}

export interface ApiResponseWithMeta<T> {
  data: T;
  retryAfterMs: number | null;
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function toUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path}`;
}

function retryAfterToMs(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseFloat(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const timestamp = Date.parse(raw);
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, timestamp - Date.now());
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return response.json();
  const text = await response.text();
  return text || null;
}

async function apiGetWithMeta<T>(path: string): Promise<ApiResponseWithMeta<T>> {
  const headers = new Headers();
  const token = await getStoredAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // Warum: roher fetch hier braucht ebenfalls ein Timeout (siehe apiRequest),
  // sonst kann ein einzelner hängender Bootstrap-Request das gesamte Setup
  // für immer im "bootstrapping"-Zustand halten.
  const abort = createTimeoutAbort(undefined, DEFAULT_API_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(toUrl(path), {
      headers,
      credentials: API_CREDENTIALS,
      signal: abort.signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError' && abort.signal.aborted) {
      throw new ApiError(0, { code: 'request_timeout' });
    }
    throw error;
  } finally {
    abort.dispose();
  }

  const data = await parseResponse(response);
  const retryAfterMs = retryAfterToMs(response.headers.get('Retry-After'));

  if (!response.ok) {
    const error = new ApiError(response.status, data) as ApiError & { retryAfterMs?: number | null };
    error.retryAfterMs = retryAfterMs;
    throw error;
  }

  return { data: data as T, retryAfterMs };
}

function withQuery(path: string, query?: QueryParams): string {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function getAuthMe(): Promise<AuthUser> {
  return apiRequest<AuthUser>('/api/auth/me');
}

export function login(identifier: string, password: string, remember?: boolean): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: remember === undefined ? { identifier, password } : { identifier, password, remember },
  });
}

export function logoutRequest(): Promise<unknown> {
  return apiRequest<unknown>('/api/auth/logout', { method: 'POST' });
}

/**
 * W1-B: Gast-Tuer. Legt ohne Formular ein Konto an (20 Start-Coins, USK bis 12)
 * und liefert wie /register einen Bearer-Token zurueck.
 */
export function guestLogin(body: GuestLoginRequest): Promise<GuestLoginResponse> {
  return apiRequest<GuestLoginResponse>('/api/auth/guest', { method: 'POST', body });
}

/**
 * W1-B: Gastkonto zu einem echten Konto aufwerten (Benutzername + Passwort,
 * E-Mail freiwillig, Geburtsdatum + Zustimmung Pflicht). Antwort enthaelt den
 * rotierten Token und die gutgeschriebenen Bonus-Coins.
 */
export function upgradeCredentials(
  body: UpgradeCredentialsRequest,
): Promise<UpgradeCredentialsResponse> {
  return apiRequest<UpgradeCredentialsResponse>('/api/auth/upgrade-credentials', {
    method: 'POST',
    body,
  });
}

export function getCampaigns(): Promise<Campaign[]> {
  return apiRequest<Campaign[]>('/api/campaigns');
}

export function getCampaign(id: number | string): Promise<Campaign> {
  return apiRequest<Campaign>(`/api/campaigns/${encodeURIComponent(String(id))}`);
}

export function getCharacters(): Promise<Character[]> {
  return apiRequest<Character[]>('/api/characters');
}

export function getCharacter(id: number | string): Promise<Character> {
  return apiRequest<Character>(`/api/characters/${encodeURIComponent(String(id))}`);
}

/**
 * W2-C/W2-B: Live-Vorschau der Begruessung im Begleiter-Editor.
 *
 * Coin-frei und ohne :id — der Charakter existiert noch nicht. Der Server
 * limitiert eigenstaendig (10/min); der Aufrufer verwirft laufende Anfragen
 * ueber `signal`, wenn der Nutzer weitertippt. Fehler (429/400/502) werden im
 * Editor bewusst STILL geschluckt — eine Vorschau ist Beiwerk, kein Pflichtweg.
 */
export function previewGreeting(
  body: PreviewGreetingRequest,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<PreviewGreetingResponse> {
  return apiRequest<PreviewGreetingResponse>('/api/characters/preview-greeting', {
    method: 'POST',
    body,
    signal: options?.signal,
    // R2-06: Der Serverpfad ist Waechter-Vorcheck + bis zu zwei Generierungen.
    // Die 25 s des Standards reichen dafuer nicht immer — ein Abbruch kurz vor
    // der Antwort waere die teuerste aller Varianten (bezahlt, nie gesehen).
    timeoutMs: options?.timeoutMs,
  });
}

/**
 * R2-06: Wuerfel neben den Feldern des Begleiter-Bogens.
 *
 * Zieht EINEN Zufallswert aus den committeten Listen in server/data/wuerfel/.
 * Kein Modellaufruf, keine Coins — die Route ist bewusst billig und darf
 * beliebig oft getippt werden (Limiter 60/min als Missbrauchsbremse).
 */
export function randomFill(
  field: RandomFillField,
  lang: string,
  options?: { signal?: AbortSignal },
): Promise<RandomFillResponse> {
  return apiRequest<RandomFillResponse>(
    withQuery('/api/characters/random-fill', { field, lang }),
    { signal: options?.signal },
  );
}

/**
 * R3-06: die Liste aller Stimm-Eigenschaften in der Zielsprache. Liegt bewusst
 * unter derselben Route wie der Wuerfel (gleicher Limiter, gleiche Auth).
 */
export function getVoiceTraits(
  lang: string,
  options?: { signal?: AbortSignal },
): Promise<VoiceTraitsResponse> {
  return apiRequest<VoiceTraitsResponse>(
    withQuery('/api/characters/random-fill/voice-traits', { lang }),
    { signal: options?.signal },
  );
}

export function getWorldElements(query?: QueryParams): Promise<WorldElement[]> {
  return apiRequest<WorldElement[]>(withQuery('/api/world-elements', query));
}

export function getWorldElement(id: number | string): Promise<WorldElement> {
  return apiRequest<WorldElement>(`/api/world-elements/${encodeURIComponent(String(id))}`);
}

export function getWorldElementCharacters(): Promise<WorldElementCharacter[]> {
  return apiRequest<WorldElementCharacter[]>('/api/world-elements/characters');
}

export function getWorldRelationshipsForSource(
  sourceType: RelationshipEndpointType,
  sourceId: number | string,
): Promise<WorldRelationship[]> {
  return apiRequest<WorldRelationship[]>(
    `/api/world-relationships/${encodeURIComponent(sourceType)}/${encodeURIComponent(String(sourceId))}`,
  );
}

export function getWorldRelationshipsForSourceWithMeta(
  sourceType: RelationshipEndpointType,
  sourceId: number | string,
): Promise<ApiResponseWithMeta<WorldRelationship[]>> {
  return apiGetWithMeta<WorldRelationship[]>(
    `/api/world-relationships/${encodeURIComponent(sourceType)}/${encodeURIComponent(String(sourceId))}`,
  );
}

// Alle Beziehungen des Users in EINEM Request — fuer den Offline-Bootstrap.
// Ersetzt das frühere N+1 (pro Quelle ein Request), das mit den Beispiel-Kosmos
// (321+ Elemente) den Erststart blockierte. Liefert dasselbe Superset wie
// getRelationshipsForElement + getRelationshipsToElement (source/target inkl.
// Name + Portrait).
export function getAllWorldRelationships(): Promise<WorldRelationship[]> {
  return apiRequest<WorldRelationship[]>('/api/world-relationships/all');
}

export function getWorldRelationshipsToTarget(
  targetType: RelationshipEndpointType,
  targetId: number | string,
): Promise<WorldRelationship[]> {
  return apiRequest<WorldRelationship[]>(
    `/api/world-relationships/to/${encodeURIComponent(targetType)}/${encodeURIComponent(String(targetId))}`,
  );
}

// Kaskade: Zieltyp → Element → Kategorie → Beziehungstyp.
// Quelle = aktuelles Welt-Element, Ziel = gewähltes Element oder Charakter.
// Server liefert nur Kategorien/Typen, die die Quell-Typ→Ziel-Typ-Kombination erlaubt.
export function getWorldRelationshipTypes(
  sourceElementType: string,
  targetElementType: string,
  sourceType: RelationshipEndpointType = 'world_element',
  targetType: RelationshipEndpointType = 'world_element',
): Promise<WorldRelationshipTypeOptions> {
  return apiRequest<WorldRelationshipTypeOptions>(
    withQuery(`/api/world-relationships/types/${encodeURIComponent(sourceElementType)}/${encodeURIComponent(targetElementType)}`, {
      sourceType,
      targetType,
    }),
  );
}

export function createWorldRelationship(
  payload: CreateWorldRelationshipInput,
): Promise<CreateWorldRelationshipResponse> {
  const body: Record<string, unknown> = {
    sourceType: payload.sourceType,
    sourceId: payload.sourceId,
    targetType: payload.targetType,
    targetId: payload.targetId,
    category: payload.category,
    relationType: payload.relationType,
    showInEditor: payload.showInEditor ?? true,
  };
  return apiRequest<CreateWorldRelationshipResponse>('/api/world-relationships', {
    method: 'POST',
    body,
  });
}

export function deleteWorldRelationship(id: number | string): Promise<unknown> {
  return apiRequest<unknown>(`/api/world-relationships/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
  });
}

export function getWorldTrees(): Promise<WorldTreeSummary[]> {
  return apiRequest<WorldTreeSummary[]>('/api/world-trees');
}

export function getWorldTree(id: number | string): Promise<WorldTreeFull> {
  return apiRequest<WorldTreeFull>(`/api/world-trees/${encodeURIComponent(String(id))}`);
}

export function getWorldTreeWithMeta(id: number | string): Promise<ApiResponseWithMeta<WorldTreeFull>> {
  return apiGetWithMeta<WorldTreeFull>(`/api/world-trees/${encodeURIComponent(String(id))}`);
}

// Hinweis: /api/narrators/available liefert ein gewrapptes Objekt
// { scope, current_tone_id, ..., narrators: [...] } — daher das Array hier
// herauslösen, statt es als nacktes Array zu behandeln (sonst droht beim
// Spread/Iterieren der gleiche "e is not iterable"-Crash wie bei Gruppenchats).
export async function getNarratorsAvailable(query?: QueryParams): Promise<WorldElement[]> {
  const res = await apiRequest<unknown>(withQuery('/api/narrators/available', query));
  if (Array.isArray(res)) return res as WorldElement[];
  if (res && typeof res === 'object' && Array.isArray((res as { narrators?: unknown }).narrators)) {
    return (res as { narrators: WorldElement[] }).narrators;
  }
  return [];
}

export function getTableRounds(): Promise<TableRound[]> {
  return apiRequest<TableRound[]>('/api/table-rounds');
}

export function getTableRound(id: number | string): Promise<TableRound> {
  return apiRequest<TableRound>(`/api/table-rounds/${encodeURIComponent(String(id))}`);
}

export function getProfile(): Promise<UserSettings & JsonRecord> {
  return apiRequest<UserSettings & JsonRecord>('/api/auth/profile');
}

export function getCoinBalance(): Promise<CoinBalance> {
  return apiRequest<CoinBalance>('/api/coins/balance');
}

export function getSyncCursor(): Promise<SyncCursor> {
  return apiRequest<SyncCursor>('/api/sync/cursor');
}

export interface SyncCatalogRequest {
  version: number;
  /** Vollstaendige Verweisliste anfordern (nach dem Laden der Nutzlast). */
  full?: boolean;
}

export function getSyncChanges(
  since: number,
  limit: number,
  catalog?: SyncCatalogRequest | null,
): Promise<SyncChangesResponse> {
  // Ohne `catalog` ist die URL byte-gleich zu frueher — der Server antwortet
  // dann exakt wie bisher (inklusive der expandierten Katalogseiten).
  const query: QueryParams = { since, limit };
  if (catalog) {
    query.catalog = catalog.version;
    if (catalog.full) query.catalog_full = 1;
  }
  return apiRequest<SyncChangesResponse>(withQuery('/api/sync/changes', query));
}

export function getCatalogManifest(): Promise<CatalogManifest> {
  return apiRequest<CatalogManifest>('/api/catalog/manifest');
}

export function getCatalogPayload(version: number): Promise<CatalogPayloadResponse> {
  // Der Inhalt bleibt immutable; der feste DTO-Parameter umgeht einmalig alte HTTP-Caches.
  return apiRequest<CatalogPayloadResponse>(withQuery(`/api/catalog/v/${encodeURIComponent(String(version))}`, { audio_dto: 1 }));
}

export function getAssetSizes(paths: string[]): Promise<AssetSizesResponse> {
  return apiRequest<AssetSizesResponse>('/api/sync/asset-sizes', {
    method: 'POST',
    body: { paths },
  });
}

// Aufgabe VIII: Weltelement zwischen Kosmen verschieben oder kopieren.
// Antwort: { moved: number } bzw. { id: number, copied: number }.
export function moveElementToKosmos(
  id: number | string,
  kosmosId: number,
): Promise<{ moved: number; removedRelationships: number }> {
  return apiRequest<{ moved: number; removedRelationships: number }>(
    `/api/world-elements/${encodeURIComponent(String(id))}/move-kosmos`,
    { method: 'POST', body: { kosmosId } },
  );
}

export function copyElementToKosmos(
  id: number | string,
  kosmosId: number,
  opts: { includeSubtree?: boolean } = {},
): Promise<{ id: number; copied: number; ids: number[] }> {
  return apiRequest<{ id: number; copied: number; ids: number[] }>(
    `/api/world-elements/${encodeURIComponent(String(id))}/copy-kosmos`,
    { method: 'POST', body: { kosmosId, includeSubtree: opts.includeSubtree } },
  );
}

// Weltelement innerhalb desselben Kosmos duplizieren (Subtree optional, neuer
// Name optional). Antwort: Erst-ID, Anzahl kopierter Knoten, alle neuen IDs.
export function duplicateElement(
  id: number | string,
  opts: { includeSubtree?: boolean; newName?: string } = {},
): Promise<{ id: number; copied: number; ids: number[] }> {
  return apiRequest<{ id: number; copied: number; ids: number[] }>(
    `/api/world-elements/${encodeURIComponent(String(id))}/duplicate`,
    { method: 'POST', body: { includeSubtree: opts.includeSubtree, newName: opts.newName } },
  );
}

export interface BulkTransferMovedFrom {
  id: number;
  fromKosmosId: number | null;
}

export interface BulkTransferResult {
  action: 'move' | 'copy';
  movedCount: number;
  copiedCount: number;
  removedRelationships: number;
  skippedSystem: number;
  movedFrom: BulkTransferMovedFrom[];
  createdIds: number[];
}

// Bulk-Transfer mehrerer Elemente zwischen Kosmen (move ODER copy).
// System-Elemente werden serverseitig uebersprungen und in `skippedSystem`
// aufgefuehrt; Beziehungen, die quer ueber Kosmen verweisen wuerden, werden
// bei move entfernt (Anzahl in `removedRelationships`).
export function bulkTransferElements(
  action: 'move' | 'copy',
  ids: number[],
  kosmosId: number,
  opts: { includeSubtree?: boolean } = {},
): Promise<BulkTransferResult> {
  return apiRequest<BulkTransferResult>('/api/world-elements/bulk-kosmos', {
    method: 'POST',
    body: { action, ids, kosmosId, includeSubtree: opts.includeSubtree },
  });
}

// Vorschau fuer move: liefert die Anzahl betroffener Nachfahren und Beziehungen,
// die der move-Befehl aufgeben/aufloesen wuerde.
export function getElementMoveImpact(
  id: number | string,
): Promise<{ descendantCount: number; removedRelationshipCount: number }> {
  return apiRequest<{ descendantCount: number; removedRelationshipCount: number }>(
    `/api/world-elements/${encodeURIComponent(String(id))}/move-impact`,
  );
}
